<template>
  <c-modal
    ref="modalRef"
    title="Indeling wijzigen"
    size="xl"
    :persistent="false"
  >
    <div class="tw-flex tw-flex-col tw-gap-4">
      <p class="tw-m-0 tw-text-sm tw-text-gray-500">
        Sleep aan de greep om te ordenen. Klik op een naam om te hernoemen.
      </p>

      <div
        ref="rootListRef"
        class="tw-flex tw-min-h-[1.25rem] tw-flex-col tw-gap-2"
      >
        <knowledge-base-layout-node
          v-for="category in tree"
          :key="category.uid"
          :node="category"
          :siblings="tree"
          :depth="0"
        />
      </div>

      <button type="button" class="kb-sort-dialog__add" @click="addCategory">
        <q-icon name="fa-solid fa-plus" size="12px" /> Categorie toevoegen
      </button>
    </div>

    <template #actions>
      <c-button
        color="transparent"
        text-color="gray"
        label="Annuleren"
        @click="onCancel"
      />
      <c-button
        :loading="saving"
        color="primary"
        label="Opslaan"
        @click="onSave"
      />
    </template>
  </c-modal>
</template>

<script setup lang="ts">
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import {
  apiTreeToNodes,
  nodesToPayload,
} from "composables/knowledge-base/useKnowledgeBaseTree";
import {
  canPlaceByDescriptor,
  moveNodeIntoContainer,
  moveNodeRelativeTo,
} from "composables/knowledge-base/knowledgeBaseTreeMove";
import type {
  KnowledgeBaseTreeCategoryNode,
  KnowledgeBaseTreeNode,
  KnowledgeBaseTreeNodeType,
} from "models/knowledge-base/knowledge-base-tree.types";
import { useQuasar } from "quasar";
import { useKnowledgeBaseStore } from "store/modules/knowledgeBaseStore";
import { onMounted, onUnmounted, ref, useTemplateRef } from "vue";
import KnowledgeBaseLayoutNode from "./KnowledgeBaseLayoutNode.vue";

const modalRef = useTemplateRef<{
  dialogRef: { hide: () => void };
  onDialogOK: () => void;
}>("modalRef");

const $q = useQuasar();
const kbStore = useKnowledgeBaseStore();
const tree = ref<KnowledgeBaseTreeNode[]>([]);
const saving = ref(false);
const rootListRef = ref<HTMLElement | null>(null);

// A single monitor owns every drop: it reads the innermost drop target and
// mutates the working tree. One monitor (not nested drag instances) is what
// keeps the recursive tree free of the null-context crashes vuedraggable hit.
let cleanupMonitor = (): void => {};

onMounted(() => {
  const monitor = monitorForElements({
    canMonitor: ({ source }) => source.data.kbDnd === true,
    onDrop: ({ source, location }) => {
      const sourceUid = source.data.uid;
      const target = location.current.dropTargets[0];
      if (typeof sourceUid !== "string" || !target) {
        return;
      }

      if (target.data.kbDropKind === "row") {
        const targetUid = target.data.uid;
        const edge = extractClosestEdge(target.data);
        if (
          typeof targetUid === "string" &&
          (edge === "top" || edge === "bottom")
        ) {
          moveNodeRelativeTo(tree.value, sourceUid, targetUid, edge);
        }
        return;
      }

      if (target.data.kbDropKind === "container") {
        const containerUid = target.data.containerUid;
        moveNodeIntoContainer(
          tree.value,
          sourceUid,
          typeof containerUid === "string" ? containerUid : null,
        );
      }
    },
  });

  // The list area itself is the top-level (level 0) drop container: it accepts
  // categories dropped into the gap or an empty list. Rows nested inside win as
  // the innermost target, so this only fires for the surrounding area.
  const cleanupRoot = rootListRef.value
    ? dropTargetForElements({
        element: rootListRef.value,
        canDrop: ({ source }) =>
          source.data.kbDnd === true &&
          canPlaceByDescriptor(
            source.data.nodeType as KnowledgeBaseTreeNodeType,
            source.data.ownsCategory === true,
            0,
          ),
        getData: () => ({ kbDropKind: "container", containerUid: null }),
      })
    : () => {};

  cleanupMonitor = combine(monitor, cleanupRoot);
});

onUnmounted(() => {
  cleanupMonitor();
});

const createUid = (): string => {
  const [random] = crypto.getRandomValues(new Uint32Array(1));
  return `kb-${Date.now()}-${random.toString(36)}`;
};

// Attach client-only UI/key state to the working copy. Mutates in place and
// returns the node so it can be used in a map. Categories start collapsed so
// the modal opens with a compact overview.
const withUiState = (node: KnowledgeBaseTreeNode): KnowledgeBaseTreeNode => {
  node.uid = createUid();
  if (node.type === "category") {
    node.isOpen = false;
    node.children = node.children.map(withUiState);
  }
  return node;
};

onMounted(async () => {
  const apiTree = await kbStore.fetchAdminTree();
  tree.value = apiTreeToNodes(apiTree).map(withUiState);
});

const addCategory = (): void => {
  const newNode: KnowledgeBaseTreeCategoryNode = {
    type: "category",
    uuid: null,
    title: "Nieuwe categorie",
    order: tree.value.length,
    children: [],
    uid: createUid(),
    isOpen: true,
    isRenaming: true,
  };
  tree.value.push(newNode);
};

// Safety net for the drag group rules: max depth is 2 (category ->
// subcategory -> articles), so a subcategory may never itself contain another
// category. A category child of a top-level category is a valid subcategory;
// only a category nested inside that subcategory is illegal.
const hasNestedCategory = (nodes: KnowledgeBaseTreeNode[]): boolean =>
  nodes.some(
    (node) =>
      node.type === "category" &&
      node.children.some(
        (child) =>
          child.type === "category" &&
          child.children.some((grandchild) => grandchild.type === "category"),
      ),
  );

const onCancel = (): void => {
  modalRef.value?.dialogRef?.hide();
};

const onSave = async (): Promise<void> => {
  if (hasNestedCategory(tree.value)) {
    $q.notify({
      color: "negative",
      message: "Een subcategorie kan geen subcategorieën bevatten",
    });
    return;
  }
  saving.value = true;
  try {
    await kbStore.saveLayout(nodesToPayload(tree.value));
    modalRef.value?.onDialogOK();
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped lang="scss">
.kb-sort-dialog {
  // Matches the "Subcategorie toevoegen" button (kb-layout-node__add) so both
  // add-buttons share the same dashed styling.
  &__add {
    @apply tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-dashed tw-border-gray-500 tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500 tw-transition hover:tw-border-primary hover:tw-text-primary;
  }
}
</style>
